import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, Heart, ArrowLeft, User, Users } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    gender: '',
    user_type: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      if (!formData.email || !formData.password || !formData.name) {
        setError('Please fill in all fields');
        return;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
      setError('');
      setStep(2);
      return;
    }

    if (!formData.gender || !formData.user_type) {
      setError('Please select your identity');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await register(formData);
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-dark flex flex-col" data-testid="register-page">
      {/* Header */}
      <header className="p-4">
        <button
          onClick={() => step === 1 ? navigate('/') : setStep(1)}
          className="p-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
      </header>

      <div className="flex-1 flex flex-col justify-center px-6 pb-12">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="max-w-sm mx-auto w-full"
        >
          {step === 1 ? (
            <>
              {/* Logo */}
              <div className="text-center mb-8">
                <Heart className="w-12 h-12 text-gold mx-auto mb-4" fill="#D4AF37" />
                <h1 className="font-heading text-3xl text-white mb-2">Create Account</h1>
                <p className="text-gray-400 font-body">Find your perfect match</p>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm font-body">
                  {error}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-dark-elevated border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                  data-testid="name-input"
                />

                <Input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-dark-elevated border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                  data-testid="email-input"
                />

                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className="bg-dark-elevated border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl pr-12"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 btn-gold rounded-full font-body font-semibold"
                  data-testid="continue-btn"
                >
                  Continue
                </Button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-gray-400 text-sm font-body">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Google Login */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full h-12 rounded-full border-white/20 text-white hover:bg-white/5 font-body"
                data-testid="google-register-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              {/* Login link */}
              <p className="text-center mt-6 text-gray-400 font-body">
                Already have an account?{' '}
                <button onClick={() => navigate('/login')} className="text-gold hover:underline">
                  Sign in
                </button>
              </p>
            </>
          ) : (
            <>
              {/* Step 2: Identity */}
              <div className="text-center mb-8">
                <h1 className="font-heading text-3xl text-white mb-2">Tell us about you</h1>
                <p className="text-gray-400 font-body">This helps us show you the right matches</p>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm font-body">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Identity Selection */}
                <div className="space-y-3">
                  <p className="text-white font-body font-medium">I am a...</p>
                  
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, gender: 'male', user_type: 'western_man' })}
                    className={`w-full p-4 rounded-xl border text-left transition-colors ${
                      formData.user_type === 'western_man'
                        ? 'bg-gold/10 border-gold text-white'
                        : 'bg-dark-elevated border-white/10 text-gray-300 hover:border-white/20'
                    }`}
                    data-testid="western-man-btn"
                  >
                    <div className="flex items-center gap-3">
                      <User className={`w-6 h-6 ${formData.user_type === 'western_man' ? 'text-gold' : ''}`} />
                      <div>
                        <p className="font-body font-semibold">Western Man</p>
                        <p className="text-sm text-gray-400">From North America, Europe, or Australia</p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, gender: 'female', user_type: 'african_woman' })}
                    className={`w-full p-4 rounded-xl border text-left transition-colors ${
                      formData.user_type === 'african_woman'
                        ? 'bg-gold/10 border-gold text-white'
                        : 'bg-dark-elevated border-white/10 text-gray-300 hover:border-white/20'
                    }`}
                    data-testid="african-woman-btn"
                  >
                    <div className="flex items-center gap-3">
                      <Users className={`w-6 h-6 ${formData.user_type === 'african_woman' ? 'text-gold' : ''}`} />
                      <div>
                        <p className="font-body font-semibold">African Woman</p>
                        <p className="text-sm text-gray-400">Living in Africa or the diaspora</p>
                      </div>
                    </div>
                  </button>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !formData.user_type}
                  className="w-full h-12 btn-gold rounded-full font-body font-semibold disabled:opacity-50"
                  data-testid="create-account-btn"
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
